import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  StatsigClient,
  StatsigProvider,
  useFeatureGate,
  useGateValue,
  useStatsigClient,
  useStatsigUser,
  type StatsigUser,
} from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { auth } from './firebase';
import { useProject } from './contexts/ProjectContext';

type MetadataValue = string | number | boolean | null | undefined;
export type StatsigMetadata = Record<string, MetadataValue>;

type AuthUser = {
  uid?: string | null;
  email?: string | null;
};

export type StatsigApi = {
  isLoading: boolean;
  isFeatureEnabled: (gateName: string, fallback?: boolean) => boolean;
  checkFeatureFlag: (gateName: string, fallback?: boolean) => boolean;
  logEvent: typeof trackEvent;
  trackEvent: typeof trackEvent;
  trackPageView: typeof trackPageView;
};

const STATSIG_CLIENT_KEY = process.env.REACT_APP_STATSIG_CLIENT_KEY?.trim() ?? '';

let clientRef: StatsigClient | null = null;
let defaultMetadata: StatsigMetadata = {};

function authUser(): AuthUser | null {
  return auth.currentUser
    ? { uid: auth.currentUser.uid, email: auth.currentUser.email }
    : null;
}

function statsigUser(user: AuthUser | null, projectId?: string): StatsigUser {
  const email = user?.email?.toLowerCase() ?? undefined;

  return {
    userID: String(user?.uid ?? email ?? 'anonymous'),
    email,
    customIDs: { firebase_uid: user?.uid ?? undefined, project_id: projectId },
    custom: { project_id: projectId, environment: process.env.NODE_ENV ?? 'development' },
  };
}

function metadata(values: StatsigMetadata): Record<string, string> {
  const path = typeof window === 'undefined' ? undefined : window.location.pathname;

  return Object.entries({ ...defaultMetadata, ...values, path: values.path ?? defaultMetadata.path ?? path })
    .reduce<Record<string, string>>((acc, [key, value]) => {
      if (value !== undefined) acc[key] = String(value);
      return acc;
    }, {});
}

export function checkFeatureFlag(gateName: string, fallback = false): boolean {
  if (!clientRef || clientRef.loadingStatus !== 'Ready') return fallback;

  try {
    return clientRef.checkGate(gateName);
  } catch (error) {
    console.warn(`Statsig gate check failed for ${gateName}:`, error);
    return fallback;
  }
}

export function trackEvent(eventName: string, values: StatsigMetadata = {}, _options?: unknown): void {
  try {
    clientRef?.logEvent(eventName, undefined, metadata(values));
  } catch (error) {
    console.warn(`Statsig log failed for ${eventName}:`, error);
  }
}

export function trackPageView(pageName: string, values: StatsigMetadata = {}): void {
  trackEvent('page_view', { page_name: pageName, ...values });
}

function StatsigRuntime(): null {
  const location = useLocation();
  const { selectedProject } = useProject();
  const { client } = useStatsigClient();
  const { updateUserAsync } = useStatsigUser();
  const [user, setUser] = useState<AuthUser | null>(authUser);

  useEffect(() => {
    return auth.onAuthStateChanged((nextUser: any) => {
      setUser(nextUser ? { uid: nextUser.uid, email: nextUser.email } : null);
    });
  }, []);

  useEffect(() => {
    clientRef = client instanceof StatsigClient ? client : null;
    return () => {
      if (clientRef === client) clientRef = null;
    };
  }, [client]);

  useEffect(() => {
    defaultMetadata = {
      project_id: selectedProject?.id,
      project_name: selectedProject?.name,
      path: location.pathname,
    };
  }, [location.pathname, selectedProject?.id, selectedProject?.name]);

  useEffect(() => {
    void updateUserAsync(statsigUser(user, selectedProject?.id)).catch((error: unknown) => {
      console.warn('Statsig user update failed:', error);
    });
  }, [selectedProject?.id, updateUserAsync, user]);

  return null;
}

export function AppStatsigProvider({ children }: { children: ReactNode }) {
  if (!STATSIG_CLIENT_KEY) return <>{children}</>;

  return (
    <StatsigProvider
      sdkKey={STATSIG_CLIENT_KEY}
      user={statsigUser(authUser())}
      options={{
        environment: { tier: process.env.NODE_ENV ?? 'development' },
        plugins: [new StatsigAutoCapturePlugin()],
      }}
    >
      <StatsigRuntime />
      {children}
    </StatsigProvider>
  );
}

export function useStatsig(): StatsigApi {
  const { checkGate, isLoading, logEvent } = useStatsigClient();

  const isFeatureEnabled = (gateName: string, fallback = false): boolean => {
    if (isLoading) return fallback;

    try {
      return checkGate(gateName);
    } catch (error) {
      console.warn(`Statsig gate check failed for ${gateName}:`, error);
      return fallback;
    }
  };

  const logStatsigEvent = (eventName: string, values: StatsigMetadata = {}, _options?: unknown): void => {
    try {
      logEvent(eventName, undefined, metadata(values));
    } catch (error) {
      console.warn(`Statsig log failed for ${eventName}:`, error);
    }
  };

  return {
    isLoading,
    isFeatureEnabled,
    checkFeatureFlag: isFeatureEnabled,
    logEvent: logStatsigEvent,
    trackEvent: logStatsigEvent,
    trackPageView: (pageName: string, values: StatsigMetadata = {}) => {
      logStatsigEvent('page_view', { page_name: pageName, ...values });
    },
  };
}

export { useFeatureGate, useGateValue, useStatsigClient };
